import { toISODate, addDaysIso } from './date.ts'

/**
 * ══ BỘ LỌC + KPI MÀN ĐƠN HÀNG — LUẬT THUẦN DÙNG CHUNG (chép `Az.tsx:2260-2410`) ═════════════════════════════
 *
 * ⭐ PHẦN LỌC TÁCH KHỎI PHẦN VẼ, và đó không phải sở thích: bản cũ có **9 chip lọc nhanh + 15 ô
 * lọc**, và luật của từng cái là một câu hỏi nghiệp vụ riêng ("đơn nào chờ vận hành", "đơn nào
 * còn nợ"). Nhét hết vào thân component thì cách duy nhất để kiểm một luật là vẽ cả màn rồi bấm.
 * Ở đây chúng là hàm thuần, kiểm thẳng được.
 *
 * ⭐ VÌ SAO NẰM Ở `@rs/contracts` (đợt Đ3, 16/10/2026): màn Đơn hàng chuyển sang lọc · đếm · KPI Ở MÁY CHỦ
 *   (`POST /az/orders/bang*`, SQL ở `@rs/order`). Hai bản của cùng một luật — bản JS này và bản SQL — chỉ giữ
 *   được khớp nhau nếu có một bài đối chứng chạy CẢ HAI trên cùng dữ liệu. Bài đó cần CSDL thật nên sống ở
 *   `apps/api`, mà `apps/api` không được đọc mã `apps/web`. Nên bản JS dời về đây — gói duy nhất cả hai đầu
 *   cùng đọc — và `apps/web/.../orders-filter.ts` · `order-kpi.ts` chỉ còn xuất lại. Đây là BẢN GỐC ĐỐI
 *   CHỨNG: sửa luật ở đây mà không sửa SQL (hoặc ngược lại) thì `order-board.test.ts` đỏ.
 *
 * ⚠ ĐẾM CHIP TRÊN TOÀN DANH SÁCH TRONG PHẠM VI, KPI TRÊN TẬP ĐANG LỌC — hai câu hỏi khác nhau, xem từng hàm.
 */

/** Chín chip lọc nhanh, ĐÚNG THỨ TỰ bản cũ (`Az.tsx:2260`). */
export const QUICK_KEYS = [
  'all', 'draft', 'today', 'tomorrow', 'noleg', 'running', 'unbilled', 'owing', 'reefer',
] as const
export type QuickKey = (typeof QUICK_KEYS)[number]

/**
 * Trường NGÀY đem ra lọc — SÁU mốc, đúng thứ tự bản cũ (`Az.tsx:1659`). Bốc / giao THỰC TẾ đọc `actualAt` của điểm lấy đầu /
 * điểm giao cuối, thiếu thì rơi về kế hoạch (`azStopDates`).
 */
export const DATE_BASES = ['planPickup', 'planDeliver', 'actualPickup', 'actualDeliver', 'created', 'invoiced'] as const
export type DateBasis = (typeof DATE_BASES)[number]

/** Nguồn đơn (Đ3): `legacy` đơn hệ RATRACO cũ · `new` đơn rs-erp. Ngoài ô này màn KHÔNG phân biệt cũ/mới. */
export const ORDER_SOURCES = ['', 'legacy', 'new'] as const
export type OrderSource = (typeof ORDER_SOURCES)[number]

/** Chọn hết danh sách đang lọc tối đa bấy nhiêu đơn (bản cũ `AZ_BULK_MAX` 250 → 200 = trần lô của máy chủ). */
export const ORDER_BULK_MAX = 200

/**
 * TRẦN SỐ DÒNG một lần dán của ba hộp nhập hàng loạt (rà 16/10 L40 — lỗi riêng rs-erp: bản cũ ghi thẳng vào kho trình duyệt
 * nên không có trần). Màn soát TRƯỚC khi gửi và báo tiếng Việt kèm cách chia lô; máy chủ vẫn giữ đúng trần này.
 */
export const BULK_ORDER_IMPORT_MAX = 500
export const BULK_REVENUE_MAX = 1000
export const BULK_COST_IMPORT_MAX = 500

export interface OrderFilters {
  quick: QuickKey
  /** Ô 1 — mã đơn / mã cont. DÁN CẢ CỘT từ Excel được: mỗi dòng một mã. */
  q: string
  /** Ô 2 — tên khách / MST. Tách khỏi ô 1 (bản cũ user 25/08) vì gõ nhiều từ ≠ dán nhiều mã. */
  qName: string
  routes: string[]
  orderStatuses: string[]
  packs: string[]
  contTypes: string[]
  customers: string[]
  billStatuses: string[]
  sales: string[]
  /** Chuyến đi: `yes` đã bắn cho ≥ 1 thầu phụ · `no` chưa. */
  trip: '' | 'yes' | 'no'
  /** Thu tiền khách: `paid` đã thanh toán đủ · `unpaid` chưa (kể cả chưa có hóa đơn). */
  paid: '' | 'paid' | 'unpaid'
  /** Trả NCC: `owe` còn khoản chưa chi · `clear` đã chi hết / chưa có khoản. */
  owe: '' | 'owe' | 'clear'
  /** MST của KHÁCH. */
  msts: string[]
  /** Đối tác (thầu phụ) đã bắn chặng. */
  partners: string[]
  /** Bảng kê MUA: `none` còn khoản chưa lập · `has` đã lập hết · `nocost` chưa nhập giá vốn. */
  stmts: string[]
  /** Nguồn đơn — xem `ORDER_SOURCES`. */
  source: OrderSource
  dateBasis: DateBasis
  from: string
  to: string
}

export const EMPTY_FILTERS: OrderFilters = {
  quick: 'all', q: '', qName: '',
  routes: [], orderStatuses: [], packs: [], contTypes: [], customers: [], billStatuses: [],
  sales: [], trip: '', paid: '', owe: '', msts: [], partners: [], stmts: [], source: '',
  dateBasis: 'planPickup', from: '', to: '',
}

/**
 * ⭐ MỞ MÀN RA LÀ ĐANG Ở THÁNG HIỆN TẠI (bản cũ `Az.tsx:1655`, chốt 24/07 sau khi để trắng một thời gian).
 *
 * Để trắng thì mở màn là kéo TOÀN BỘ lịch sử đơn: danh sách dài, và mọi ô KPI + chip đếm nhanh đều
 * tính trên cả kho chứ không phải kỳ đang làm — người dùng đọc ra những con số không phải của tháng này.
 *
 * ⚠ `EMPTY_FILTERS` GIỮ NGUYÊN rỗng: nó là mốc để biết "người dùng có đang lọc gì không"
 *   (`hasAnyFilter`) và là thứ nút ✕ Xóa lọc trả về — xóa lọc nghĩa là xem hết, đúng ý người bấm.
 */
export const monthFilters = (todayIso: string): OrderFilters => {
  const [y = '', m = ''] = todayIso.split('-')
  const last = new Date(Number(y), Number(m), 0).getDate()
  return { ...EMPTY_FILTERS, from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` }
}

export const hasAnyFilter = (f: OrderFilters): boolean =>
  f.quick !== 'all' || f.q.trim() !== '' || f.qName.trim() !== ''
  || f.routes.length > 0 || f.orderStatuses.length > 0 || f.packs.length > 0
  || f.contTypes.length > 0 || f.customers.length > 0 || f.billStatuses.length > 0
  || f.sales.length > 0 || f.from !== '' || f.to !== ''
  || f.trip !== '' || f.paid !== '' || f.owe !== '' || f.msts.length > 0 || f.partners.length > 0 || f.stmts.length > 0
  || f.source !== ''

/** So mã thì bỏ hết ký tự không phải chữ-số: người dùng dán mã có dấu cách, gạch, xuống dòng. */
export const normCode = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '')

/** Tách ô tìm thành danh sách mã. Một mã ⇒ tìm MỜ; nhiều mã ⇒ khớp ĐÚNG từng mã. */
export const codeList = (q: string): string[] =>
  q.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s !== '')

/**
 * Đúng những ô của một dòng đơn mà bộ lọc đọc tới. `OrderDto` (web) và `Order` (`@rs/order`) đều khớp khuôn này.
 * ⚠ `| undefined` tường minh ở ô tuỳ chọn: kho bật `exactOptionalPropertyTypes`, và `legacy` của `OrderDto` là
 *   `.optional()` (bản hợp đồng cũ không có nó).
 */
export interface BoardOrder {
  ref: string
  contNo: string | null
  customerId: string
  routeName: string | null
  orderStatus: string
  packId: string | null
  contType: string | null
  billStatus?: string | null | undefined
  ownerUserId: string | null
  planDate: string
  dueAt: string | null
  pickups: readonly unknown[] | null
  drops: readonly unknown[] | null
  createdAt: string
  draft?: boolean | undefined
  legacy?: boolean | undefined
}

/** Khách — đúng ba ô mà ô "tên khách / MST" và ô MST đọc tới. */
export interface BoardCustomer {
  id: string
  name: string
  code: string | null
  tax: string | null
  /** SALE CHÍNH của khách — bộ lọc "NVKD" gom theo đây (bản cũ `Az.tsx:1640`). Vắng = chưa gán. */
  salesMainId?: string | null | undefined
}

/**
 * Cont LẠNH. Bản cũ nhận diện bằng chính chữ người dùng gõ vào ô loại cont (`Az.tsx` `isReefer`) —
 * không có cờ riêng trong dữ liệu, và ba cách viết đều gặp thật: `RF`, `lạnh`, `lanh`.
 */
export const isReefer = (o: { contType: string | null }): boolean => /rf|lạnh|lanh/i.test(o.contType ?? '')

/**
 * Trạng thái TIỀN của một đơn, suy từ hóa đơn AR gắn với nó.
 * `null` = KHÔNG BIẾT — đơn chưa có hóa đơn, hoặc người đang xem không đọc được sổ công nợ.
 */
export type PayState = 'paid' | 'partial' | 'unpaid'
export type PaidLookup<T extends BoardOrder = BoardOrder> = (o: T) => PayState | null

/**
 * Một đơn có khớp chip lọc nhanh không.
 *
 * ⚠ HAI CHIP LỆCH CÁCH TÍNH so với bản cũ, và lệch có lý do chứ không phải làm tắt:
 *   · `noleg` "chờ vận hành" — bản cũ đếm SỐ CHẶNG của đơn (`azLegProgress(...).total === 0`).
 *     rs-erp không có cửa đọc chặng cho cả danh sách (chặng đọc theo TỪNG đơn), và gọi một cửa
 *     cho mỗi dòng là hàng trăm lời gọi để vẽ một con số. Thay bằng `orderStatus` — chính trường
 *     mà rs-erp dùng để mô tả bước đang ở đâu: chưa ai nhận chạy ⇒ `pending`/`confirmed`.
 *   · `running` "đang chạy" — bản cũ suy từ chặng; ở đây là ba bước giữa của `orderStatus`.
 *   Hai cách cho CÙNG một câu trả lời trong mọi luồng bình thường, vì `orderStatus` của rs-erp
 *   tiến theo chặng. Ghi ở đây để ngày ai đó thấy lệch thì biết ngay chỗ phải nhìn.
 */
export const matchQuick = <T extends BoardOrder>(o: T, quick: QuickKey, paidOf: PaidLookup<T>): boolean => {
  const today = toISODate()
  switch (quick) {
    case 'all': return o.draft !== true
    case 'draft': return o.draft === true
    case 'today': return o.planDate === today
    case 'tomorrow': return o.planDate === addDaysIso(today, 1)
    case 'noleg': return o.orderStatus === 'pending' || o.orderStatus === 'confirmed'
    case 'running':
      return o.orderStatus === 'picking' || o.orderStatus === 'transiting'
        || o.orderStatus === 'awaiting_delivery'
    case 'unbilled': return (o.billStatus ?? 'none') !== 'issued'
    case 'owing': { const p = paidOf(o); return p === 'unpaid' || p === 'partial' }
    case 'reefer': return isReefer(o)
    default: return true
  }
}

/** Ngày của tờ hóa đơn AR gắn với đơn — `null` khi chưa xuất hoặc không đọc được sổ. */
export type InvoiceDateLookup<T extends BoardOrder = BoardOrder> = (o: T) => string | null

/** Sự thật vận hành / giá vốn của một đơn — từ bảng tóm tắt điều hành (`POST /az/dieu-hanh/tom-tat`). */
export interface BoardFacts {
  trips: number
  vendorIds: readonly string[]
  /** `null` khi người xem không được thấy giá vốn. */
  cost: {
    costCount: number; confirmedCount: number; unpaidCount: number; stmtState: '' | 'none' | 'has'
  } | null
}

export interface FilterDeps<T extends BoardOrder = BoardOrder> {
  customers: readonly BoardCustomer[]
  /** `null` khi chưa tải xong bảng tóm tắt — lọc theo chuyến / đối tác / NCC / bảng kê khi đó không loại đơn nào. */
  boardOf?: (o: T) => BoardFacts | null
  /** Trạng thái tiền của đơn — `() => null` khi không đọc được sổ công nợ. */
  paidOf: PaidLookup<T>
  /** Ngày hóa đơn — chỉ dùng khi lọc theo mốc `invoiced`. */
  invoicedOf: InvoiceDateLookup<T>
}

/** Ngày đem ra so, theo mốc người dùng chọn. `createdAt` là dấu thời gian ⇒ cắt lấy 10 ký tự. */
const stopActual = (stops: readonly unknown[] | null, last: boolean): string => {
  const list = (stops ?? []) as Record<string, unknown>[]
  const s = last ? list[list.length - 1] : list[0]
  return typeof s?.['actualAt'] === 'string' ? s['actualAt'].slice(0, 10) : ''
}
const dateOf = <T extends BoardOrder>(o: T, basis: DateBasis, invoicedOf: InvoiceDateLookup<T>): string => {
  if (basis === 'planDeliver') return o.dueAt ?? ''
  if (basis === 'actualPickup') return stopActual(o.pickups, false) || o.planDate
  if (basis === 'actualDeliver') return stopActual(o.drops, true) || (o.dueAt ?? '')
  if (basis === 'created') return o.createdAt.slice(0, 10)
  if (basis === 'invoiced') return invoicedOf(o) ?? ''
  return o.planDate
}

export const filterOrders = <T extends BoardOrder>(
  rows: readonly T[], f: OrderFilters, deps: FilterDeps<T>,
): T[] => {
  const cusOf = (id: string): BoardCustomer | undefined => deps.customers.find((c) => c.id === id)
  const codes = codeList(f.q)
  const qName = f.qName.trim().toLowerCase()

  return rows.filter((o) => {
    if (!matchQuick(o, f.quick, deps.paidOf)) return false
    if (f.source !== '' && (o.legacy === true) !== (f.source === 'legacy')) return false

    if (codes.length === 1) {
      // MỘT mã ⇒ tìm MỜ: người dùng đang gõ dở, chưa chắc nhớ hết mã.
      const needle = normCode(codes[0] as string)
      const hay = `${normCode(o.ref)} ${normCode(o.contNo ?? '')}`
      if (!hay.includes(needle)) return false
    } else if (codes.length > 1) {
      // NHIỀU mã (dán từ Excel) ⇒ khớp ĐÚNG: tìm mờ ở đây sẽ kéo về những đơn không ai hỏi.
      const want = new Set(codes.map(normCode))
      if (!want.has(normCode(o.ref)) && !want.has(normCode(o.contNo ?? ''))) return false
    }

    if (qName !== '') {
      const c = cusOf(o.customerId)
      const hay = `${c?.name ?? ''} ${c?.code ?? ''} ${c?.tax ?? ''}`.toLowerCase()
      if (!hay.includes(qName)) return false
    }

    if (f.routes.length > 0 && !f.routes.includes(o.routeName ?? '')) return false
    if (f.orderStatuses.length > 0 && !f.orderStatuses.includes(o.orderStatus)) return false
    if (f.packs.length > 0 && !f.packs.includes(o.packId ?? '')) return false
    if (f.contTypes.length > 0 && !f.contTypes.includes(o.contType ?? '')) return false
    if (f.customers.length > 0 && !f.customers.includes(o.customerId)) return false
    if (f.billStatuses.length > 0 && !f.billStatuses.includes(o.billStatus ?? 'none')) return false
    // "NVKD" = SALE CHÍNH CỦA KHÁCH (bản cũ `Az.tsx:1640` `custOf(j.customerId)?.salesMainId`), KHÔNG phải người tạo đơn.
    if (f.sales.length > 0 && !f.sales.includes(cusOf(o.customerId)?.salesMainId ?? '')) return false
    if (f.msts.length > 0 && !f.msts.includes(cusOf(o.customerId)?.tax ?? '')) return false
    if (f.paid !== '') {
      const p = deps.paidOf(o)
      if ((p === 'paid') !== (f.paid === 'paid')) return false
    }
    const b = deps.boardOf?.(o) ?? null
    if (b !== null) {
      if (f.trip !== '' && (b.trips > 0) !== (f.trip === 'yes')) return false
      if (f.partners.length > 0 && !f.partners.some((v) => b.vendorIds.includes(v))) return false
      if (b.cost !== null) {
        if (f.owe !== '' && (b.cost.costCount > 0 && b.cost.unpaidCount > 0) !== (f.owe === 'owe')) return false
        const st = b.cost.stmtState === '' ? 'nocost' : b.cost.stmtState
        if (f.stmts.length > 0 && !f.stmts.includes(st)) return false
      }
    }

    const d = dateOf(o, f.dateBasis, deps.invoicedOf)
    if (f.from !== '' && (d === '' || d < f.from)) return false
    if (f.to !== '' && (d === '' || d > f.to)) return false
    return true
  })
}

/**
 * Số trên từng chip lọc nhanh.
 *
 * ⚠ ĐẾM TRÊN TOÀN DANH SÁCH, KHÔNG phải trên kết quả đang lọc — đúng bản cũ. Con số ở đây trả lời
 *   *"bấm vào sẽ thấy bao nhiêu đơn"*; đếm trên kết quả đã lọc thì mọi chip khác đều hiện 0 và
 *   người dùng tưởng không còn gì.
 */
export const quickCounts = <T extends BoardOrder>(
  rows: readonly T[], paidOf: PaidLookup<T>,
): Record<QuickKey, number> => {
  const out = {} as Record<QuickKey, number>
  /* ⛔ KHÔNG ĐẾM ĐƠN HỆ CŨ (anh chốt 21/10: "không cần cộng đơn hệ cũ"). Bản cũ không có đơn hệ cũ — chip của nó đếm đơn của
     chính hệ thống; đơn di trú RATRACO cũ là phần rs-erp mới phát sinh, cộng vào làm "Tất cả 13.120 · Cont lạnh 1.999" vô nghĩa
     với việc hằng ngày. Đơn cũ vẫn xem / lọc được (ô "Nguồn đơn"), chỉ không vào số trên chip. */
  const own = rows.filter((o) => o.legacy !== true)
  for (const k of QUICK_KEYS) out[k] = own.filter((o) => matchQuick(o, k, paidOf)).length
  return out
}

/**
 * Dán NHIỀU mã: bao nhiêu mã, thiếu mã nào trong danh sách ĐANG LỌC (bản cũ `Az.tsx:2303`). Một mã (đang gõ dở) ⇒ `null`.
 */
export const pastedCodes = (
  q: string, rows: readonly { ref: string; contNo: string | null }[],
): { pasted: number; missing: string[] } | null => {
  const codes = codeList(q)
  if (codes.length <= 1) return null
  const found = new Set(rows.flatMap((o) => [normCode(o.ref), normCode(o.contNo ?? '')].filter((x) => x !== '')))
  return { pasted: codes.length, missing: codes.filter((c) => !found.has(normCode(c))) }
}

/* ══ KPI NHANH CỦA DANH SÁCH ĐƠN — luật thuần (chép dải `az.kpi.*` của bản cũ) ════════════════════════════════
 *
 * ⭐ ĐỌC TRÊN DANH SÁCH ĐANG LỌC. Sáu con số này trả lời *"cái tôi đang nhìn đang chạy thế nào"* —
 *   lọc một khách, một tuyến, một tháng thì KPI phải đi theo. Một dải KPI tính trên cả kho không
 *   đổi khi người dùng lọc, và khi đó nó chỉ là trang trí.
 *
 * ⚠ TIỀN CHỈ HIỆN VỚI NGƯỜI XEM ĐƯỢC GIÁ VỐN. `costVnd`/`profitVnd` do máy chủ trả `null` cho
 *   người khác (`OpsBoardDto.cost` nullable) — ở đây quy về `null`, KHÔNG quy về 0: "không được
 *   xem" và "bằng 0" là hai chuyện khác nhau.
 */

/** Đúng những ô của một dòng đơn mà dải KPI đọc tới. */
export interface OrderForKpi {
  orderStatus: string
  /** Hạn giao cam kết (`YYYY-MM-DD`); `null` = không cam kết hạn. */
  dueAt: string | null
  sellPrice: string
}

/** Phần tóm tắt vận hành của một đơn — `null` khi máy chủ chưa trả (hoặc không được xem giá vốn). */
export interface BoardForKpi {
  legCount: number
  costVnd: number | null
  unpaidVnd: number | null
}

export interface OrderKpi {
  /** Đơn đã quá hạn giao mà CHƯA giao xong. */
  lateCount: number
  /** Đơn đang trên đường (đã bắt đầu, chưa giao, chưa huỷ). */
  runningCount: number
  /** Đơn CHƯA có chặng nào — chưa giao cho nhà thầu. */
  noLegCount: number
  sellVnd: number
  /** `null` ⇒ người xem không được thấy giá vốn. */
  buyVnd: number | null
  profitVnd: number | null
  /** Biên lãi gộp theo %, một chữ số. `null` khi chưa biết giá vốn hoặc chưa có doanh thu. */
  marginPct: number | null
  unpaidVnd: number | null
}

const DONE: readonly string[] = ['delivered', 'cancelled']
/** Đơn đã rời bậc "mới nhận" nhưng chưa xong — đúng dải trạng thái bản cũ gọi là "đang chạy". */
const RUNNING: readonly string[] = ['picking', 'in_transit', 'at_drop']

export const orderKpi = (
  rows: readonly OrderForKpi[], boardOf: (i: number) => BoardForKpi | null, todayIso: string,
): OrderKpi => {
  let sellVnd = 0
  let buyVnd = 0
  let unpaidVnd = 0
  let costKnown = false
  let lateCount = 0
  let runningCount = 0
  let noLegCount = 0

  rows.forEach((o, i) => {
    sellVnd += Number(o.sellPrice) || 0
    if (o.dueAt !== null && o.dueAt < todayIso && !DONE.includes(o.orderStatus)) lateCount += 1
    if (RUNNING.includes(o.orderStatus)) runningCount += 1
    const b = boardOf(i)
    if (b !== null && b.legCount === 0 && o.orderStatus !== 'cancelled') noLegCount += 1
    if (b?.costVnd != null) { costKnown = true; buyVnd += b.costVnd }
    if (b?.unpaidVnd != null) unpaidVnd += b.unpaidVnd
  })

  const profitVnd = costKnown ? sellVnd - buyVnd : null
  return {
    lateCount, runningCount, noLegCount, sellVnd,
    buyVnd: costKnown ? buyVnd : null,
    profitVnd,
    marginPct: profitVnd === null || sellVnd <= 0 ? null : Math.round((profitVnd / sellVnd) * 1000) / 10,
    unpaidVnd: costKnown ? unpaidVnd : null,
  }
}

/**
 * THU HỘ của danh sách đang lọc (bản cũ `obTotal`, `Az.tsx:2831`) — nâng hạ · cầu đường chi hộ
 * khách. Khách vẫn trả, nhưng KHÔNG phải doanh thu và KHÔNG chịu VAT, nên đứng thành một ô
 * riêng chứ không cộng vào ô Doanh thu. (Dời từ thân `OrdersTab` ra đây để có bản gốc đối chứng.)
 */
export const onBehalfTotal = (rows: readonly { revenues: readonly unknown[] | null }[]): number =>
  rows.reduce((sum, o) => sum + (o.revenues ?? []).reduce<number>((s, line) => {
    const l = line as Record<string, unknown>
    if (l['kind'] !== 'onbehalf') return s
    const amt = Number(l['amount'])
    return s + (Number.isFinite(amt) ? amt : 0)
  }, 0), 0)
